import Drawer from 'components/Drawer';
import { BodySemi, Capitalize, CaptionRegular } from 'components/Text';
import { TableStack } from '../RelatedHashtagsTable/styledComponents';
import { DataTable } from '../DataTable';
import DownloadButton from '../../Button/DownloadButton';
import FlavorGroups from './FlavorGroups';
import FlavorsDrawer from './FlavorsDrawer';
import { IconButton } from '../../Button';
import { OverflowEllipsis } from 'utils/fragments';
import { RelatedHashtagsTableHeadColFilter } from '../DataTable/styledComponents';
import Stack from 'components/Stack';
import TableHeading from 'components/TableHeading';
import { createDefaultColumns } from '../DataTable/utils';
import { downloadItemsAsCSV } from 'utils/formatCSV';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import useFlavorsByCategory from './useFlavorsByCategory';
import useFlavorsTableProps from './useFlavorsTableProps';

const ParentEntity = styled(CaptionRegular)`
    max-width: 100%;
    ${OverflowEllipsis};
`;

const formatCell = ({ value, row }) => {
    return (
        <Stack $space="-.25rem" $direction="column">
            <ParentEntity as="div">{row?.relatedName}</ParentEntity>
            <BodySemi as="div">{value}</BodySemi>
        </Stack>
    );
};

const getColumnsConfig = (visibleOrderedMetrics) => {
    const columnsConfig = createDefaultColumns(
        ['name', 'parentName', ...visibleOrderedMetrics],
        { showHealthStatLabels: true },
    );
    columnsConfig[0].formatCell = formatCell;
    columnsConfig[1].formatCell = ({ value }) => (
        <Capitalize>{value}</Capitalize>
    );
    return columnsConfig;
};

function FlavorsTable({
    parentEntity,
    parentEntityId,
    category,
    country,
    dataSource,
    week,
    year,
    icon,
    title,
    subtitle,
    count,
    hideCount = false,
    tools,
    tooltipKey = 'flavorsTitleHelp',
    tooltipExample,
    filename = 'flavors table',
    name,
    ...restProps
}) {
    const { query: countQuery } = useFlavorsByCategory({
        category: category ? [category] : null,
        country: country ? [country] : null,
        dataSource,
        per_page: 1,
        enabled: Boolean(category && country && dataSource),
    });
    const totalRecords = countQuery.isPreviousData
        ? null
        : (countQuery.data?.pages?.[0]?.total_records ?? null);
    const {
        columnsMenu,
        dataReady,
        isLoading,
        noDataMessage,
        rows,
        sortedData,
        filters,
        setFilters,
        setFilter,
        filtersKey,
        sortOption,
        setSortOption,
        sortOptionKey,
        maxSortMetric,
        visibleColumnsConfig,
        handleSearchInputChange,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
        getQueryData,
        initialSortOption,
    } = useFlavorsTableProps({
        category,
        country,
        dataSource,
        name,
        getColumnsConfig,
    });

    return (
        <>
            <TableHeading
                icon={icon}
                title={title}
                subtitle={subtitle}
                {...(!hideCount && { count: totalRecords })}
                tooltipKey={tooltipKey}
                tooltipExample={tooltipExample}
                tools={
                    <>
                        {tools}
                        <DownloadButton
                            ButtonComponent={IconButton}
                            disabled={!dataReady}
                            isLoading={isLoading}
                            onClick={() => {
                                toast.promise(
                                    (async () => {
                                        try {
                                            const columnsConfigCopy =
                                                visibleColumnsConfig.map(
                                                    (column) => ({ ...column }),
                                                );
                                            const allData =
                                                await getQueryData();
                                            downloadItemsAsCSV(
                                                filename,
                                                allData,
                                                columnsConfigCopy,
                                            );
                                        } catch (error) {
                                            console.error(
                                                'Error downloading CSV:',
                                                error,
                                            );
                                            throw error;
                                        }
                                    })(),
                                    {
                                        loading: 'Preparing download...',
                                        success: 'Download complete!',
                                        error: 'Error downloading CSV',
                                    },
                                );
                            }}
                            mixpanelMeta={{ contentKey: 'HashtagDataTable' }}
                        />
                    </>
                }
            />
            <TableStack>
                <div
                    css={`
                        display: flex;
                        flex-wrap: nowrap;
                        gap: 0.75rem;
                        align-items: flex-start;
                    `}
                >
                    <div
                        css={`
                            flex-grow: 1;
                        `}
                    >
                        <FlavorGroups
                            filters={filters}
                            setFilter={setFilter}
                            setFilters={setFilters}
                            filtersKey={filtersKey}
                            category={category}
                            country={country}
                            dataSource={dataSource}
                            order_by={initialSortOption?.sortBy}
                            ascending={initialSortOption?.ascending}
                        />
                    </div>
                    {columnsMenu}
                </div>
                <DataTable
                    isLoading={isLoading}
                    isFetching={isFetching}
                    fetchNextPage={fetchNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    data={sortedData}
                    dataKey={`d-*-s-${sortOptionKey}-f-${filtersKey}`}
                    noDataMessage={noDataMessage}
                    sortOption={sortOption}
                    setSortOption={setSortOption}
                    maxSortMetric={maxSortMetric}
                    name={name}
                    onSearchInputChange={handleSearchInputChange}
                    DrawerComponent={Drawer}
                    TableHeadColFilter={RelatedHashtagsTableHeadColFilter}
                    columns={visibleColumnsConfig}
                    week={week}
                    year={year}
                    rowHeight={60}
                    renderDrawerContent={(selectedId) => {
                        const row = rows.find((row) => row.id === selectedId);
                        return (
                            <FlavorsDrawer
                                country={country}
                                dataSource={dataSource}
                                category={category}
                                flavor={row?.flavor}
                                flavorName={row?.name}
                            />
                        );
                    }}
                    {...restProps}
                />
            </TableStack>
        </>
    );
}

export default FlavorsTable;
